/**
 * F31 (Wave 2B) — admin skill-disputes queue page.
 *
 * Route: `/skill-disputes`
 * Permission: `skill_disputes:resolve` (super_admin + support).
 *
 * Chip-row status filter (Open / Resolved / All via `?status=`); default = Open
 * (the actionable queue). Each row links to the detail page where the resolve
 * action lives. Mirrors the F23 commission-statements queue shape.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chip,
  EmptyState,
  Pagination,
  Skeleton,
  Stat,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import type { MatchOutcomeStatus } from '@bravemove/types';
import {
  adminSkillDisputesApi,
  type AdminSkillDisputeRow,
  type ListDisputesOpts,
} from '../../api/skillDisputes';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';
import { usePageKpis } from '../../hooks/usePageKpis';

const PAGE_SIZE = 25;

type UiFilter = 'disputed' | 'resolved_by_admin' | 'all';
const DEFAULT_FILTER: UiFilter = 'disputed';

const FILTER_TABS: ReadonlyArray<{ value: UiFilter; labelKey: string; defaultLabel: string }> = [
  { value: 'disputed', labelKey: 'admin:skill.disputes.tab_open', defaultLabel: 'Open' },
  {
    value: 'resolved_by_admin',
    labelKey: 'admin:skill.disputes.tab_resolved',
    defaultLabel: 'Resolved',
  },
  { value: 'all', labelKey: 'admin:skill.disputes.tab_all', defaultLabel: 'All' },
];

const STATUS_CHIP_MAP: Record<MatchOutcomeStatus, StatusChipStatus> = {
  pending_confirmation: 'pending',
  confirmed: 'approved',
  disputed: 'in_review',
  resolved_by_admin: 'approved',
  expired: 'rejected',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminSkillDisputesPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterParam = (searchParams.get('status') as UiFilter | null) ?? DEFAULT_FILTER;
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;

  const [rows, setRows] = useState<readonly AdminSkillDisputeRow[]>([]);
  const [total, setTotal] = useState(0);
  // F61 (A3) — one unaudited `GET /admin/page-kpis` (was 2–4 audited
  // `perPage=1` list reads); refreshed after any admin action.
  const kpiCounts = usePageKpis('skill_disputes');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: ListDisputesOpts = { status: filterParam, page: pageParam, perPage: PAGE_SIZE };
      const data = await adminSkillDisputesApi.listDisputes(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [filterParam, pageParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onFilterChange = (value: UiFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_FILTER) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<AdminSkillDisputeRow>[]>(
    () => [
      {
        key: 'sport',
        header: t('admin:skill.disputes.column.sport', { defaultValue: 'Sport' }),
        cell: (row) => (
          <span className="text-[13px] text-ink capitalize">{row.sport.replace(/-/g, ' ')}</span>
        ),
      },
      {
        key: 'status',
        header: t('admin:skill.disputes.column.status', { defaultValue: 'Status' }),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={STATUS_CHIP_MAP[row.status]}
            label={t(`skill:outcome.status.${row.status}`)}
          />
        ),
      },
      {
        key: 'reason',
        header: t('admin:skill.disputes.column.reason', { defaultValue: 'Dispute reason' }),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2 line-clamp-1 max-w-[28ch]">
            {row.disputeReason ?? '—'}
          </span>
        ),
      },
      {
        key: 'disputed_at',
        header: t('admin:skill.disputes.column.disputed_at', { defaultValue: 'Disputed' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.disputedAt, locale)}</span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6" data-testid="skill-disputes-page">
      <PageHeader
        title={t('admin:skill.disputes.page_title', { defaultValue: 'Skill disputes' })}
        subtitle={t('admin:skill.disputes.page_subtitle', {
          defaultValue: 'Review and resolve disputed match results.',
        })}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3" data-testid="skill-disputes-kpi-strip">
        <Stat
          label={t('admin:skill.disputes.kpi.open', { defaultValue: 'Open disputes' })}
          value={kpiCounts === null ? '—' : kpiCounts.open}
        />
        <Stat
          label={t('admin:skill.disputes.kpi.resolved', { defaultValue: 'Resolved disputes' })}
          value={kpiCounts === null ? '—' : kpiCounts.resolved}
        />
      </div>

      <div
        role="tablist"
        aria-label={t('admin:skill.disputes.page_title', { defaultValue: 'Filter by status' })}
        className="flex flex-wrap items-center gap-2"
      >
        {FILTER_TABS.map((tab) => (
          <Chip
            key={tab.value}
            selected={filterParam === tab.value}
            onClick={() => onFilterChange(tab.value)}
            data-testid={`skill-disputes-tab-${tab.value}`}
          >
            {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
          </Chip>
        ))}
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="skill-disputes-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:skill.disputes.empty', {
            defaultValue: 'No skill disputes in this view.',
          })}
        />
      ) : (
        <>
          <Table
            data={rows as AdminSkillDisputeRow[]}
            columns={columns as TableColumn<AdminSkillDisputeRow>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/skill-disputes/${row.id}`)}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination page={pageParam} pageCount={pageCount} onChange={onPageChange} />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default AdminSkillDisputesPage;
